import { useState } from 'react'
import { useRequest, useView } from '@10000/react'
import { SettingsForm } from '../'
import { Avatar, AvatarDialog, Body, Box, Button, Dialog, H4, ItemPill } from '../../widgets'
import { Text, DropDown } from '../../forms'

export const User = ({ selectedUser, onClose }) => {
  const request = useRequest('tenant')
  const { users, teams } = useView('tenant')
  const { avatar, admin, id, name } = users.find(x => x.id === selectedUser) || { }

  const userTeams = teams.filter(x => x.leaders?.includes(id))
  const isTeamLead = userTeams.length > 0
  const onlyAdmin = admin && users.filter(user => user.admin).length < 2

  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false)
  const [selectedAvatar, setSelectedAvatar] = useState(avatar)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const avatarSelected = (avatar) => {
    setSelectedAvatar(avatar)
    setAvatarDialogOpen(false)
  }

  const deleteUser = () => {
    if(!onlyAdmin) {
      return request('userDeleted', { id }).then(() => {
        onClose()
      })
    }
    setDeleteDialogOpen(false)
  }

  const onSubmit = ({ name, admin }) => request('userUpdated', { id: selectedUser, avatar: selectedAvatar, name, admin: admin === 'Admin' }).then(onClose)

  return (
    <SettingsForm
      onSubmit={onSubmit}
      values={{ name, admin: admin ? 'Admin' : 'Team lead' }}
      title={name || 'Unnamed'}
      onBackButtonClick={onClose}
    >
      <Box gap={16} alignItems="center" justifyContent="flex-start" fullWidth>
        <Avatar
          index={selectedAvatar}
          label={admin ? 'ADMIN' : 'TEAM LEAD'}
          labelColor="white"
          labelBackground={admin ? 'red' : 'blue'}
        />
        <Button className="small" onClick={() => setAvatarDialogOpen(true)}>
          Change avatar
        </Button>
      </Box>

      <Text title="Name" name="name" value={name} required dark/>

      <DropDown
        fullWidth
        dark
        name="admin"
        title="Role"
        required
        options={[{ label: 'Admin', value: 'Admin' }, { label: 'Team lead', value: 'Team lead' }]}
      />

      <Body alt bold>Teams</Body>
      {userTeams.map(({ name, avatar }, index) =>
        <ItemPill action={avatar === undefined} index={index} key={index} blackListBackgrounds={['white']}>
          <Box fullWidth alignItems="center" gap={8}>
            <Avatar size="small" index={avatar} mode="team"/>
            <Box fullWidth>
              <Body bold align="left">{name ? name : 'Unnamed Team'}</Body>
            </Box>
          </Box>
        </ItemPill>
      )}
      {userTeams.length === 0 && (
        <ItemPill action>
          <Box fullWidth alignItems="center" gap={8}>
            <Avatar size="small" mode="team"/>
            <Box fullWidth>
              <Body bold align="left">No teams</Body>
            </Box>
          </Box>
        </ItemPill>
      )}

      <Box fullWidth p="32px 0" justifyContent="flex-start">
        <Box onClick={() => setDeleteDialogOpen(true)} cursor="pointer">
          <Body underline bold alt italic>Delete User</Body>
        </Box>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} showControls={false} mobileBackground="white" mobileDrawer={false}>
        <H4 color="black">{onlyAdmin ? 'Unable to delete user' : 'Delete user'}</H4>
        {onlyAdmin
          ? <Body color="black">
              Unable to delete this user as they are the only admin. You must assign another admin before deleting this user.
            </Body>
          : <>
              <Body color="black">
                Deleting this user will unlink their data. If they rejoin, it will be as a new user with separate data.
              </Body>
              {isTeamLead && <Body color="black">
                Teams assigned to this user will no longer have a team leader when this user is deleted.
              </Body>}
            </>
        }
        <Box fullWidth gap={8}>
          {!onlyAdmin && <Button fullWidth light onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>}
          <Button fullWidth onClick={deleteUser}>{onlyAdmin ? 'Okay' : 'Delete'}</Button>
        </Box>
      </Dialog>
      <AvatarDialog
        value={avatar}
        open={avatarDialogOpen}
        onClose={avatarSelected}
      />
    </SettingsForm>
  )
}